import React, { useState, useEffect } from 'react'
import {
  Typography, Spin, Table, Card, Button, message, Row, Col, Input
} from 'antd'
import ComplaintModal from './ComplaintModal'
import { complaintsService } from '../services/complaintsService';
import { formatBrowser } from '../utils/dateUtils';
import { useSession } from '../context/SessionContext'
import useComplaints from '../hooks/useComplaints';
import useTableSearchPagination from '../hooks/useTableSearchPagination';
import { useGlobalLoading } from '../context/LoadingContext';


const { Title, Paragraph } = Typography

export default function ComplaintsView() {
  const { complaints, loading, fetchComplaints } = useComplaints();
  const { hasRole } = useSession()

  const [modalOpen, setModalOpen] = useState(false)
  const [mode, setMode] = useState('create')
  const [selectedComplaint, setSelectedComplaint] = useState(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(false)
  const [resolving, setResolving] = useState(false)
  const { startLoading, stopLoading } = useGlobalLoading();

  useEffect(() => {
    fetchComplaints()
  }, [])

  const columns = [
    { title: 'Complaint Number', dataIndex: 'id', key: 'id' },
    { title: 'Community', dataIndex: 'community', key: 'community' },
    { title: 'Makani Number', dataIndex: 'makaniNumber', key: 'makaniNumber', render: v => v || '-' },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <span>({record.latitude}, {record.longitude})</span>
      )
    },
    {
      title: 'Timestamp',
      dataIndex: 'date',
      key: 'date',
      render: value => value ? formatBrowser(value, { seconds: true }) : '-'
    },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      key: 'createdBy'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status =>
        status === 1
          ? <span style={{ color: '#52c41a' }}>Resolved</span>
          : <span style={{ color: '#faad14' }}>Pending</span>
    },
    {
      title: 'Resolved by',
      dataIndex: 'resolvedBy',
      key: 'resolvedBy',
      render: v => v || '-'
    },
    {
      title: 'Resolved at',
      dataIndex: 'resolvedAt',
      key: 'resolvedAt',
      render: v => v ? formatBrowser(v, { seconds: true }) : '-'
    }
  ]

  const filterComplaint = (c, searchText) => {
    const q = searchText.toLowerCase();
    const statusText = c.status === 1 ? 'resolved' : 'pending';

    return (
      String(c.id).includes(q) ||
      (c.community || '').toLowerCase().includes(q) ||
      (c.makaniNumber || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q) ||
      statusText.includes(q) ||
      (c.date ? formatBrowser(c.date, { seconds: true }).toLowerCase().includes(q) : false) ||
      (c.createdBy || '').toLowerCase().includes(q) ||
      (c.resolvedBy || '').toLowerCase().includes(q) ||
      (c.resolvedAt ? formatBrowser(c.resolvedAt, { seconds: true }).toLowerCase().includes(q) : false)
    );
  };

  const {
    searchText,
    onSearchChange,
    filteredData: filteredComplaints,
    pagination
  } = useTableSearchPagination({
    data: complaints,
    filterFn: filterComplaint
  });

  // acciones
  const openCreateModal = () => {
    setMode('create')
    setSelectedComplaint(null)
    setModalOpen(true)
  }

  const openEditModal = record => {
    setMode('edit')
    setSelectedComplaint(record)
    setModalOpen(true)
  }

  const handleCreate = async (values, form) => {
    try {
      startLoading();
      setCreating(true)
      await complaintsService.create(values);
      message.success('Complaint created successfully')
      setModalOpen(false)
      form.resetFields()
      fetchComplaints()
    } catch {
      message.error('Failed to create complaint')
    } finally {
      stopLoading();
      setCreating(false)
    }
  }

  const handleUpdate = async (values) => {
    try {
      startLoading();
      setEditing(true)
      await complaintsService.update(selectedComplaint.id, values);
      message.success('Complaint updated successfully')
      setModalOpen(false)
      fetchComplaints()
    } catch {
      message.error('Failed to update complaint')
    } finally {
      stopLoading();
      setEditing(false)
    }
  }

  const handleResolve = async (id, form) => {
    setResolving(true);
    startLoading();
    try {
      await complaintsService.resolve(id, form.getFieldValue('observations'));
      message.success('Complaint marked as resolved');
      setModalOpen(false);
      fetchComplaints();
    } catch {
      message.error('Failed to mark as resolved');
    } finally {
      setResolving(false);
      stopLoading();
    }
  };

  const handleReopen = async (id) => {
    try {
      startLoading();
      await complaintsService.reopen(id);
      message.success('Complaint reopened');
      setModalOpen(false)
      fetchComplaints()
    } catch {
      message.error('Failed to reopen complaint')
    } finally {
      stopLoading();
    }
  }

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <Row style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Complaints</Title>
        </Col>
      </Row>

      <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Input
            placeholder="Search complaints..."
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
            allowClear
            style={{ maxWidth: 300 }}
          />
        </Col>
        <Col>
          {!hasRole('COMPLAINTS_VIEW') && (
            <Button type="primary" onClick={openCreateModal}>
              Create Complaint
            </Button>
          )}
        </Col>
      </Row>

      { !filteredComplaints.length ? (
        <Paragraph style={{ marginTop: 40 }}>
          No complaints found.
        </Paragraph>
      ) : (
        <Card style={{ marginTop: 12 }}>
          <Table
            rowKey="id"
            dataSource={filteredComplaints}
            columns={columns}
            pagination={pagination}
            size="small"
            onRow={record => ({
              onClick: () => openEditModal(record),
              style: { cursor: 'pointer' }
            })}
          />
        </Card>
      )}

      <ComplaintModal
        open={modalOpen}
        mode={mode}
        initialValues={selectedComplaint}
        status={selectedComplaint?.status || 0}
        creating={creating}
        editing={editing}
        resolving={resolving}
        onCancel={() => setModalOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onResolve={handleResolve}
        onReopen={handleReopen}
        readOnly={hasRole('COMPLAINTS_VIEW')}
      />
    </div>
  )
}
